import {
  type ContractDto,
  consentsFor,
  ESIGN_PAPER_ISSUED_MESSAGE,
  ESIGN_EVENT_LABELS,
  ESIGN_ORDERS,
  ESIGN_SIGNER_STATUSES,
  ESIGN_STATUSES,
  type EsignCompanySignerDto,
  type EsignRequestDto,
  type EsignStatus,
  type PartyDto,
} from '@bqcrm/shared';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Checkbox,
  Collapse,
  Group,
  Modal,
  NumberInput,
  Radio,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  TextInput,
  Timeline,
} from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconClock, IconMail, IconMessage, IconSignature, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { api, errorText } from '../../api';
import { useMe } from '../../auth';
import { useCompanies } from '../../data';
import { formatDateTime } from '../../format';
import { notifyError, notifyOk } from '../../notify';
import { ask } from '../../confirm';
import { ReasonDialog } from '../planning/common';

const STATUS_COLOR: Record<EsignStatus, string> = { przygotowuje: 'gray', w_toku: 'blue', finalizacja: 'cyan', zakonczony: 'green', odrzucony: 'red', anulowany: 'gray', wygasly: 'orange' };
const SIGNER_COLOR: Record<string, string> = { oczekuje: 'gray', podpisal: 'green', odmowil: 'red' };

/** Wysłanie dokumentu do podpisu elektronicznego: podpisujący po stronie klienta, kanał kodu, osoba reprezentująca firmę. */
export function SendDialog({ k, onClose }: { k: ContractDto; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const party = useQuery({ queryKey: ['party', k.payer.id], queryFn: () => api<PartyDto>(`/parties/${k.payer.id}`) });
  const signers = useQuery({ queryKey: ['esign-signers', k.id], queryFn: () => api<EsignCompanySignerDto[]>(`/contracts/${k.id}/esign/company-signers`) });
  const p = party.data;
  const [form, setForm] = useState<{
    name: string | null;
    email: string | null;
    phone: string | null;
    channel: 'sms' | 'email';
    consumer: boolean | null;
    companySignerId: string | null;
    order: 'klient_pierwszy' | 'firma_pierwsza';
    expiresDays: number;
    message: string;
  }>({ name: null, email: null, phone: null, channel: 'sms', consumer: null, companySignerId: null, order: 'klient_pierwszy', expiresDays: 14, message: '' });
  // wartości domyślne z karty płatnika (dopóki użytkownik ich nie zmieni)
  const name = form.name ?? (p ? (p.kind === 'osoba' ? `${p.firstName} ${p.lastName}`.trim() : (p.contacts[0]?.fullName ?? '')) : '');
  const email = form.email ?? p?.emails[0] ?? p?.contacts.find((c) => c.email)?.email ?? '';
  const phone = form.phone ?? p?.phones[0] ?? p?.contacts.find((c) => c.phone)?.phone ?? '';
  const consumer = form.consumer ?? p?.kind === 'osoba';
  // domyślnie podpisuje osoba wysyłająca (jeśli ma uprawnienie reprezentacji), inaczej pierwsza z listy
  const signerId = form.companySignerId ?? signers.data?.find((s) => s.id === me?.id)?.id ?? signers.data?.[0]?.id ?? null;

  const draft = k.status === 'roboczy';
  // QA P3: wydana na papier — treść ma zdanie o egzemplarzach i linie podpisu odręcznego (zamrożona przy wydaniu)
  const paperIssued = k.status === 'wydany' && k.issueMode === 'papier';
  const [paperAccepted, setPaperAccepted] = useState(false);
  const navigate = useNavigate();
  const reissue = useMutation({
    mutationFn: () => api<ContractDto>(`/contracts/${k.id}/reissue-esign`, { method: 'POST' }),
    onSuccess: (n) => {
      notifyOk(`Wydano nową wersję ${n.number ?? ''} do podpisu elektronicznego — wyślij ją do podpisu`);
      void qc.invalidateQueries({ queryKey: ['contract'] });
      void qc.invalidateQueries({ queryKey: ['case-contracts'] });
      onClose();
      navigate(`/umowy/${n.id}?esign=1`);
    },
    onError: notifyError,
  });
  const send = useMutation({
    mutationFn: async () => {
      // jeden krok: umowa robocza jest najpierw wydawana (numer, zamrożenie treści), potem wysyłana do podpisu
      if (draft) await api<ContractDto>(`/contracts/${k.id}/issue`, { method: 'POST', body: { esign: true } });
      try {
        return await api<EsignRequestDto[]>(`/contracts/${k.id}/esign`, {
          method: 'POST',
          body: { client: { name, email, phone, channel: form.channel, consumer }, companySignerId: signerId, order: form.order, expiresDays: form.expiresDays, message: form.message, paperTextAccepted: paperIssued && paperAccepted },
        });
      } catch (e) {
        if (draft) throw new Error(`Umowa została wydana, ale wysyłka do podpisu się nie powiodła: ${errorText(e)}. Wyślij ją ponownie z karty „Podpis elektroniczny”.`);
        throw e;
      }
    },
    onSuccess: (data) => {
      qc.setQueryData(['esign', k.id], data);
      notifyOk(`${draft ? 'Wydano umowę i wysłano' : 'Wysłano'} ${form.order === 'klient_pierwszy' ? 'link do podpisu klientowi' : 'do podpisu — najpierw podpis firmy'}`);
      onClose();
    },
    onError: (e) => {
      notifyError(e);
      if (draft) onClose();
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['contract', k.id] });
      void qc.invalidateQueries({ queryKey: ['case-contracts'] });
    },
  });

  return (
    <Stack>
      {draft && (
        <Alert color="orange" variant="light">
          Umowa zostanie <b>wydana</b> (otrzyma numer, treść i załączniki zostaną zamrożone) i od razu wysłana do podpisu elektronicznego.
        </Alert>
      )}
      {paperIssued && (
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />} title="Umowa wydana do podpisu papierowego">
          <Stack gap="xs">
            <Text size="sm">{ESIGN_PAPER_ISSUED_MESSAGE}</Text>
            <Text size="sm">
              „Wydaj ponownie do e-podpisu” tworzy nową wersję z nowym numerem (ta zostaje w historii jako zastąpiona) i wydaje ją z treścią dla podpisu elektronicznego.
            </Text>
            <Group>
              <Button size="xs" leftSection={<IconSignature size={14} />} loading={reissue.isPending} onClick={() => reissue.mutate()}>
                Wydaj ponownie do e-podpisu
              </Button>
            </Group>
            <Checkbox size="xs" label="Wyślij tę treść bez zmian (klient podpisze elektronicznie dokument przygotowany do podpisu odręcznego)" checked={paperAccepted} onChange={(e) => setPaperAccepted(e.currentTarget.checked)} />
          </Stack>
        </Alert>
      )}
      <Alert color="blue" variant="light">
        Podpis elektroniczny <b>zwykły</b>: podpisujący czyta dokument pod bezpiecznym linkiem, składa oświadczenia i potwierdza podpis jednorazowym kodem. Po podpisach
        obu stron system dołącza kartę podpisów i pieczętuje PDF pieczęcią firmy. To nie jest podpis kwalifikowany.
      </Alert>
      <Text fw={600} size="sm">
        Podpisujący po stronie klienta
      </Text>
      <TextInput label="Imię i nazwisko" required value={name} onChange={(e) => setForm({ ...form, name: e.currentTarget.value })} />
      <Group grow>
        <TextInput label="E-mail (na niego trafi link)" value={email} onChange={(e) => setForm({ ...form, email: e.currentTarget.value })} />
        <TextInput label="Telefon (kod SMS)" value={phone} onChange={(e) => setForm({ ...form, phone: e.currentTarget.value })} />
      </Group>
      <Radio.Group label="Kod potwierdzenia" value={form.channel} onChange={(v) => setForm({ ...form, channel: v as 'sms' | 'email' })}>
        <Group mt={4}>
          <Radio value="sms" label="SMS" />
          <Radio value="email" label="E-mail" />
        </Group>
      </Radio.Group>
      <Switch
        label="Klient jest konsumentem (oświadczenie o prawie odstąpienia i zgoda na wcześniejszy start prac)"
        checked={consumer}
        onChange={(e) => setForm({ ...form, consumer: e.currentTarget.checked })}
      />
      <Select
        label="Podpis w imieniu firmy"
        description="Osoby z uprawnieniem „Podpis elektroniczny w imieniu firmy”"
        data={(signers.data ?? []).map((s) => ({ value: s.id, label: `${s.fullName} (${s.email})` }))}
        value={signerId}
        onChange={(v) => setForm({ ...form, companySignerId: v })}
        nothingFoundMessage="Brak osób z uprawnieniem reprezentacji"
      />
      <Group grow align="flex-start">
        <Select label="Kolejność" data={Object.entries(ESIGN_ORDERS).map(([value, label]) => ({ value, label }))} value={form.order} onChange={(v) => setForm({ ...form, order: v as typeof form.order })} />
        <NumberInput label="Ważność linku (dni)" min={1} max={30} value={form.expiresDays} onChange={(v) => setForm({ ...form, expiresDays: Number(v) || 14 })} />
      </Group>
      <Textarea label="Wiadomość do klienta (opcjonalnie)" autosize minRows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.currentTarget.value })} />
      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>
          Anuluj
        </Button>
        <Button leftSection={<IconSignature size={16} />} onClick={() => send.mutate()} loading={send.isPending} disabled={!signerId || name.length < 3 || !email || (paperIssued && !paperAccepted)}>
          {draft ? 'Wydaj i wyślij do podpisu' : 'Wyślij do podpisu'}
        </Button>
      </Group>
    </Stack>
  );
}

/** Podpis w imieniu firmy przez zalogowaną osobę: oświadczenia + kod e-mail. */
function CompanySignDialog({ k, req, onClose }: { k: ContractDto; req: EsignRequestDto; onClose: () => void }) {
  const qc = useQueryClient();
  const me = req.signers.find((s) => s.role === 'firma')!;
  const companies = useCompanies();
  const companyName = companies.data?.find((c) => c.id === k.companyId)?.fullName ?? 'firmy';
  const defs = consentsFor('firma', { number: k.number ?? '', companyName, consumer: false });
  const [typedName, setTypedName] = useState(me.name);
  const [consents, setConsents] = useState<string[]>([]);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const sendCode = useMutation({
    mutationFn: () => api<{ destinationMasked: string }>(`/esign/${req.id}/company/code`, { method: 'POST' }),
    onSuccess: (r) => {
      setSentTo(r.destinationMasked);
      setError(null);
    },
    onError: (e) => setError(errorText(e)),
  });
  const sign = useMutation({
    mutationFn: () => api<EsignRequestDto[]>(`/esign/${req.id}/company/sign`, { method: 'POST', body: { typedName, consents, code } }),
    onSuccess: (data) => {
      qc.setQueryData(['esign', k.id], data);
      void qc.invalidateQueries({ queryKey: ['contract', k.id] });
      notifyOk(data[0]?.status === 'zakonczony' ? 'Podpisano — dokument został zapieczętowany' : 'Podpisano w imieniu firmy');
      onClose();
    },
    onError: (e) => setError(errorText(e)),
  });
  return (
    <Stack>
      <Text size="sm">
        Podpisujesz dokument <b>{k.number}</b> w imieniu firmy. Przed podpisem przeczytaj{' '}
        <Anchor href={`/api/contracts/${k.id}/pdf`} target="_blank">
          dokument PDF
        </Anchor>
        .
      </Text>
      <TextInput label="Imię i nazwisko" required value={typedName} onChange={(e) => setTypedName(e.currentTarget.value)} />
      {defs.map((d) => (
        <Checkbox
          key={d.code}
          label={d.text}
          checked={consents.includes(d.code)}
          onChange={(e) => {
            const on = e.currentTarget.checked;
            setConsents((x) => (on ? [...x, d.code] : x.filter((c) => c !== d.code)));
          }}
        />
      ))}
      {sentTo && (
        <TextInput
          label={`Kod wysłany na ${sentTo}`}
          value={code}
          onChange={(e) => setCode(e.currentTarget.value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
        />
      )}
      {error && (
        <Alert color="red" icon={<IconAlertTriangle />}>
          {error}
        </Alert>
      )}
      <Group justify="space-between">
        {sentTo ? (
          <Button variant="subtle" size="xs" onClick={() => sendCode.mutate()} loading={sendCode.isPending}>
            Wyślij kod ponownie
          </Button>
        ) : (
          <span />
        )}
        {sentTo ? (
          <Button onClick={() => sign.mutate()} loading={sign.isPending} disabled={code.length !== 6 || consents.length < defs.length}>
            Podpisuję
          </Button>
        ) : (
          <Button onClick={() => sendCode.mutate()} loading={sendCode.isPending} disabled={consents.length < defs.length || typedName.trim().length < 3}>
            Wyślij kod na e-mail
          </Button>
        )}
      </Group>
    </Stack>
  );
}

function RequestCard({ k, req, current }: { k: ContractDto; req: EsignRequestDto; current: boolean }) {
  const qc = useQueryClient();
  const [showEvents, setShowEvents] = useState(current && req.status !== 'zakonczony');
  const [dialog, setDialog] = useState<'company' | 'signer' | null>(null);
  const [newSigner, setNewSigner] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const { data: me } = useMe();
  const run = useMutation({
    mutationFn: (fn: () => Promise<EsignRequestDto[]>) => fn(),
    onSuccess: (data) => {
      qc.setQueryData(['esign', k.id], data);
      void qc.invalidateQueries({ queryKey: ['contract', k.id] });
      setDialog(null);
    },
    onError: notifyError,
  });
  const firm = req.signers.find((s) => s.role === 'firma');
  const firmWaiting = req.status === 'w_toku' && !!firm?.isNext;
  // kolej firmy — „Przypomnij” zamiast nowego linku (podpisujący w imieniu firmy dostaje e-mail i powiadomienie w dzwonku)
  const firmTurn = firmWaiting ? firm : undefined;
  const signers = useQuery({
    queryKey: ['esign-signers', k.id],
    queryFn: () => api<EsignCompanySignerDto[]>(`/contracts/${k.id}/esign/company-signers`),
    enabled: req.can.changeCompanySigner,
  });
  const meCanSign = !!me && firm?.userId !== me.id && (signers.data ?? []).some((s) => s.id === me.id);
  return (
    <Card withBorder padding="md" mb="md" style={current ? undefined : { opacity: 0.75 }}>
      <Group justify="space-between" align="flex-start" wrap="wrap">
        <Group gap="xs">
          <IconSignature size={18} />
          <Text fw={600}>Podpis elektroniczny</Text>
          <Badge color={STATUS_COLOR[req.status]} variant="light">
            {ESIGN_STATUSES[req.status]}
          </Badge>
        </Group>
        <Group gap="xs">
          {req.can.signAsCompany && (
            <Button size="xs" leftSection={<IconSignature size={14} />} onClick={() => setDialog('company')}>
              Podpisz w imieniu firmy
            </Button>
          )}
          {req.can.resend && !firmTurn && (
            <Button
              size="xs"
              variant="light"
              loading={run.isPending}
              onClick={() => void ask('Poprzedni link przestanie działać.', { title: 'Wysłać nowy link?', confirm: 'Wyślij' }).then((ok) => ok && run.mutate(() => api(`/esign/${req.id}/resend`, { method: 'POST' }), { onSuccess: () => notifyOk('Wysłano nowy link do podpisu') }))}
            >
              Wyślij link ponownie
            </Button>
          )}
          {req.can.resend && firmTurn && (
            <Button
              size="xs"
              variant="light"
              loading={run.isPending}
              onClick={() =>
                void ask(`${firmTurn.name} dostanie e-mail i powiadomienie w dzwonku.`, { title: 'Przypomnieć o podpisie w imieniu firmy?', confirm: 'Przypomnij' }).then(
                  (ok) => ok && run.mutate(() => api(`/esign/${req.id}/resend`, { method: 'POST' }), { onSuccess: () => notifyOk(`Wysłano przypomnienie: ${firmTurn.name}`) }),
                )
              }
            >
              Przypomnij
            </Button>
          )}
          {req.can.cancel && (
            <Button
              size="xs"
              variant="subtle"
              color="red"
              onClick={() => setCancelling(true)}
            >
              Anuluj podpisywanie
            </Button>
          )}
          {cancelling && (
            <ReasonDialog
              title="Anulowanie podpisywania"
              label="Powód (zobaczą go strony podpisu)"
              confirm="Anuluj podpisywanie"
              color="red"
              busy={run.isPending}
              onClose={() => setCancelling(false)}
              onConfirm={(reason) =>
                run.mutate(() => api(`/esign/${req.id}/cancel`, { method: 'POST', body: { reason } }), {
                  onSuccess: () => {
                    setCancelling(false);
                    notifyOk('Anulowano podpisywanie');
                  },
                })
              }
            />
          )}
          {req.status === 'zakonczony' && (
            <Button size="xs" variant="light" color="green" component="a" href={`/api/esign/${req.id}/document`} target="_blank">
              Pobierz podpisany dokument
            </Button>
          )}
        </Group>
      </Group>
      <Text size="xs" c="dimmed" mt={4}>
        Wysłano {formatDateTime(req.createdAt)} ({req.createdByName}) · {ESIGN_ORDERS[req.order].toLowerCase()} · link ważny do {formatDateTime(req.expiresAt)}
        {req.cancelReason ? ` · powód anulowania: ${req.cancelReason}` : ''}
      </Text>
      {firmWaiting && !req.can.signAsCompany && (
        <Alert color="yellow" variant="light" mt="sm" icon={<IconClock size={18} />}>
          <Text size="sm">
            {req.signers.some((s) => s.role === 'klient' && s.status === 'podpisal') ? 'Klient podpisał. ' : ''}Czeka na podpis w imieniu firmy: <b>{firm!.name}</b> ({firm!.email}). Przycisk „Podpisz w imieniu firmy” widzi tylko ta osoba po zalogowaniu.
          </Text>
          {req.can.changeCompanySigner && (
            <Group gap="xs" mt="xs">
              {meCanSign && (
                <Button size="xs" loading={run.isPending} onClick={() => run.mutate(() => api(`/esign/${req.id}/company/signer`, { method: 'POST', body: { userId: me!.id } }), { onSuccess: () => notifyOk('Zmieniono osobę podpisującą') })}>
                  Podpiszę sam(a)
                </Button>
              )}
              <Button size="xs" variant="default" onClick={() => setDialog('signer')}>
                Zmień osobę podpisującą
              </Button>
            </Group>
          )}
        </Alert>
      )}
      <Table.ScrollContainer minWidth={560} mt="sm">
      <Table verticalSpacing={6}>
        <Table.Tbody>
          {req.signers.map((s) => (
            <Table.Tr key={s.id}>
              <Table.Td w={30}>
                {s.status === 'podpisal' ? <IconCheck size={18} color="var(--mantine-color-green-7)" /> : s.status === 'odmowil' ? <IconX size={18} color="var(--mantine-color-red-7)" /> : <IconClock size={18} color="var(--mantine-color-gray-6)" />}
              </Table.Td>
              <Table.Td>
                <Text size="sm" fw={500}>
                  {s.name}
                </Text>
                <Text size="xs" c="dimmed">
                  {s.role === 'klient' ? `Klient${s.consumer ? ' (konsument)' : ''}` : 'W imieniu firmy'} · kod {s.channel === 'sms' ? `SMS ${s.phone}` : `e-mail ${s.email}`}
                </Text>
              </Table.Td>
              <Table.Td>
                <Badge size="sm" variant="light" color={SIGNER_COLOR[s.status]}>
                  {ESIGN_SIGNER_STATUSES[s.status]}
                </Badge>
                {s.isNext && (
                  <Badge size="sm" variant="outline" ml={4}>
                    teraz
                  </Badge>
                )}
              </Table.Td>
              <Table.Td>
                <Text size="xs" c="dimmed">
                  {s.signedAt ? `${formatDateTime(s.signedAt)} · IP ${s.ip ?? '—'}` : s.declineReason ? `Powód: ${s.declineReason}` : s.role === 'klient' ? `otwarcia linku: ${s.viewCount}` : ''}
                </Text>
                {s.consents.some((c) => c.code === 'wczesny_start' && c.accepted) && (
                  <Badge size="xs" color="orange" variant="light">
                    żąda startu prac przed upływem 14 dni
                  </Badge>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      </Table.ScrollContainer>
      <Group gap="xs" mt="xs">
        <Button size="compact-xs" variant="subtle" onClick={() => setShowEvents((x) => !x)}>
          {showEvents ? 'Ukryj przebieg' : `Przebieg (${req.events.length})`}
        </Button>
        <Text size="xs" c="dimmed" ff="monospace">
          Skrót treści (SHA-256): {req.contentHash.slice(0, 16)}…{req.contentHash.slice(-8)} · skrót pliku PDF: {req.documentSha256.slice(0, 16)}…{req.finalSha256 ? ` · podpisany plik: ${req.finalSha256.slice(0, 16)}…` : ''}
        </Text>
      </Group>
      <Collapse expanded={showEvents}>
        <Timeline active={req.events.length} bulletSize={18} lineWidth={2} mt="sm">
          {req.events.map((e) => (
            <Timeline.Item
              key={e.id}
              bullet={e.type === 'kod_wyslany' ? <IconMessage size={10} /> : e.type.startsWith('link') ? <IconMail size={10} /> : undefined}
              color={e.type === 'podpis' || e.type === 'zakonczenie' ? 'green' : e.type === 'kod_bledny' || e.type === 'odmowa' || e.type === 'finalizacja_blad' ? 'red' : 'blue'}
              title={<Text size="sm">{ESIGN_EVENT_LABELS[e.type] ?? e.type}</Text>}
            >
              <Text size="xs" c="dimmed">
                {formatDateTime(e.at)}
                {e.signerName ? ` · ${e.signerName}` : ''}
                {e.ip ? ` · IP ${e.ip}` : ''}
                {typeof e.details.na === 'string' ? ` · na ${e.details.na}` : ''}
                {typeof e.details.powod === 'string' ? ` · ${e.details.powod}` : ''}
              </Text>
            </Timeline.Item>
          ))}
        </Timeline>
      </Collapse>
      <Modal opened={dialog === 'company'} onClose={() => setDialog(null)} title="Podpis w imieniu firmy">
        {dialog === 'company' && <CompanySignDialog k={k} req={req} onClose={() => setDialog(null)} />}
      </Modal>
      <Modal opened={dialog === 'signer'} onClose={() => setDialog(null)} title="Osoba podpisująca w imieniu firmy">
        <Stack>
          <Select
            label="Podpisuje"
            description="Osoby z uprawnieniem „Podpis elektroniczny w imieniu firmy”. Kody wysłane poprzedniej osobie przestaną działać."
            data={(signers.data ?? []).map((s) => ({ value: s.id, label: `${s.fullName} (${s.email})` }))}
            value={newSigner ?? firm?.userId ?? null}
            onChange={setNewSigner}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDialog(null)}>
              Anuluj
            </Button>
            <Button
              loading={run.isPending}
              disabled={!newSigner || newSigner === firm?.userId}
              onClick={() => run.mutate(() => api(`/esign/${req.id}/company/signer`, { method: 'POST', body: { userId: newSigner } }), { onSuccess: () => notifyOk('Zmieniono osobę podpisującą') })}
            >
              Zmień osobę
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}

/** Panel podpisu elektronicznego na stronie umowy / aneksu. */
export function EsignPanel({ k }: { k: ContractDto }) {
  const q = useQuery({
    queryKey: ['esign', k.id],
    queryFn: () => api<EsignRequestDto[]>(`/contracts/${k.id}/esign`),
    enabled: k.status !== 'roboczy',
    // podgląd odświeża się, gdy klient podpisuje
    refetchInterval: (query) => ((query.state.data ?? []).some((r) => r.status === 'w_toku' || r.status === 'finalizacja') ? 10_000 : false),
  });
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(false);
  const qc = useQueryClient();
  const reqs = q.data ?? [];
  const current = reqs[0];
  const active = current && (current.status === 'w_toku' || current.status === 'finalizacja');
  const finished = current?.status === 'zakonczony' && k.status === 'wydany';
  useEffect(() => {
    // klient lub firma podpisali w innym oknie — odśwież stan umowy
    if (finished) void qc.invalidateQueries({ queryKey: ['contract', k.id] });
  }, [finished, qc, k.id]);
  if (k.status === 'roboczy' || (!reqs.length && !k.can.esign)) return null;

  return (
    <>
      {current && (current.status !== 'anulowany' || k.status === 'wydany') && <RequestCard k={k} req={current} current />}
      {!active && k.can.esign && (
        <Card withBorder padding="md" mb="md">
          <Group justify="space-between">
            <div>
              <Text fw={600}>Podpis elektroniczny</Text>
              <Text size="sm" c="dimmed">
                Wyślij dokument do podpisu online — klient podpisuje kodem SMS lub e-mail, firma w systemie. Zamiast druku i skanu.
              </Text>
            </div>
            <Button leftSection={<IconSignature size={16} />} onClick={() => setOpen(true)}>
              Wyślij do podpisu elektronicznego
            </Button>
          </Group>
        </Card>
      )}
      {reqs.length > 1 && (
        <>
          <Button size="compact-xs" variant="subtle" mb="xs" onClick={() => setHistory((h) => !h)}>
            {history ? 'Ukryj wcześniejsze próby' : `Wcześniejsze próby podpisu (${reqs.length - 1})`}
          </Button>
          <Collapse expanded={history}>
            {reqs.slice(1).map((r) => (
              <RequestCard key={r.id} k={k} req={r} current={false} />
            ))}
          </Collapse>
        </>
      )}
      <Modal opened={open} onClose={() => setOpen(false)} title="Wyślij do podpisu elektronicznego" size="lg">
        {open && <SendDialog k={k} onClose={() => setOpen(false)} />}
      </Modal>
    </>
  );
}
